import { CurrencyPipe, PercentPipe } from '@angular/common'
import { Component } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { WjGridModule } from '@grapecity/wijmo.angular2.grid'
import { WjGridFilterModule } from '@grapecity/wijmo.angular2.grid.filter'
import { CellType } from '@grapecity/wijmo.grid'

import { BaseComponent, Grid } from '../../../shared'
import { ActionToolbarSearchComponent } from '../../../shared/reusable-components/action-toolbar-search/action-toolbar-search.component'
import { GridExportComponent } from '../../../shared/reusable-components/grid-export/grid-export.component'
import { GridSelectionCalculationComponent } from '../../../shared/reusable-components/grid-selection-calculation/grid-selection-calculation.component'

interface MaxOverheadCategory {
    id: string
    category: string
    bold: boolean
    wages: number | null
    liabilities: number | null
    totalCost: number
    percent: number | null
}

interface MaxOverheadEmployee {
    id: string
    employee: number
    name: string
    prDept: string
    position: string
    category: string
}

@Component({
    selector: 'max-overhead-rates',
    templateUrl: './max-overhead-rates.component.html',
    standalone: true,
    imports: [FormsModule, CurrencyPipe, PercentPipe, WjGridModule, WjGridFilterModule, ActionToolbarSearchComponent, GridExportComponent, GridSelectionCalculationComponent],
})
export class MaxOverheadRatesComponent extends BaseComponent {
    year = 0
    closedThrough = ''
    revenue = 0
    totalCost = 0
    totalPercent: number | null = null
    chargedTotalCost = 0
    chargedTotalPercent: number | null = null
    loadError = ''
    categoryGrid: Grid<MaxOverheadCategory>
    employeeGrid: Grid<MaxOverheadEmployee>

    constructor() {
        super()
        this.categoryGrid = new Grid<MaxOverheadCategory>(this.services, 'id', this).wrapColumnHeaders()
        this.employeeGrid = new Grid<MaxOverheadEmployee>(this.services, 'id', this)
            .filterBy(item => [item.name, item.prDept, item.position, item.category, String(item.employee)])
            .wrapColumnHeaders()
        this.load()
    }

    load() {
        this.loadError = ''
        this.cancelableRequest(
            'maxOverheadRates',
            true,
            this.services.data.load(true, true, this.services.data.get('/api/BidRate/GetMaxOverheadRates')).subscribe({
                next: result => {
                    const payload = result[0] as any
                    this.year = payload.year
                    this.closedThrough = payload.closedThrough ?? ''
                    this.revenue = payload.revenue ?? 0
                    this.totalCost = payload.totalCost ?? 0
                    this.totalPercent = payload.totalPercent ?? null
                    this.chargedTotalCost = payload.chargedTotalCost ?? 0
                    this.chargedTotalPercent = payload.chargedTotalPercent ?? null
                    this.categoryGrid.items = (payload.categories ?? []).map((row: any) => ({
                        id: row.category,
                        category: row.category,
                        bold: !!row.bold,
                        wages: row.wages ?? null,
                        liabilities: row.liabilities ?? null,
                        totalCost: row.totalCost ?? 0,
                        percent: row.percent ?? null,
                    }))
                    this.employeeGrid.items = (payload.employees ?? []).map((row: any) => ({
                        id: String(row.employee),
                        employee: row.employee,
                        name: row.name,
                        prDept: row.prDept,
                        position: row.position,
                        category: row.category,
                    }))
                    this.initialLoadingComplete()
                },
                error: () => {
                    this.loadError = 'Max FOO could not be loaded.'
                    this.initialLoadingComplete()
                },
            }),
        )
    }

    get closedLabel(): string {
        const date = this.toMonthDate(this.closedThrough)
        if (!date) return ''
        return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
    }

    categoryFormatter = (panel: any, row: number, _col: number, cell: HTMLElement) => {
        if (panel.cellType !== CellType.Cell) return
        const item = panel.rows[row]?.dataItem as MaxOverheadCategory | undefined
        cell.style.fontWeight = item?.bold ? '700' : '400'
    }

    private toMonthDate(value: string | Date | null | undefined): Date | null {
        if (!value) return null
        if (value instanceof Date) return isNaN(value.getTime()) ? null : value
        if (value.length < 7) return null
        const [year, month] = value.substring(0, 7).split('-').map(part => Number(part))
        if (!year || !month) return null
        return new Date(year, month - 1, 1)
    }
}
